'use client'

import { cva } from 'class-variance-authority'
import { Drawer as DrawerPrimitive } from 'vaul'
import React, { createContext, useContext } from 'react'
import type { VariantProps } from 'class-variance-authority'
import { cn } from '@/utils'

type DrawerDirection = 'top' | 'bottom' | 'left' | 'right'

const DrawerDirectionContext = createContext<DrawerDirection>('bottom')

const useDrawerDirection = () => useContext(DrawerDirectionContext)

type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root> & {
  /** Direction from which the drawer opens. Default: "bottom" */
  direction?: DrawerDirection
}

const Drawer = ({
  shouldScaleBackground = true,
  direction = 'bottom',
  ...props
}: DrawerProps) => (
  <DrawerDirectionContext.Provider value={direction}>
    <DrawerPrimitive.Root
      shouldScaleBackground={shouldScaleBackground}
      direction={direction}
      {...props}
    />
  </DrawerDirectionContext.Provider>
)
Drawer.displayName = 'Drawer'

type DrawerTriggerProps = React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Trigger
>

const DrawerTrigger: React.FC<DrawerTriggerProps> = DrawerPrimitive.Trigger

type DrawerCloseProps = React.ComponentPropsWithoutRef<
  typeof DrawerPrimitive.Close
>

const DrawerClose: React.FC<DrawerCloseProps> = DrawerPrimitive.Close

const drawerOverlayVariants = cva('fixed inset-0 z-50 bg-black/80', {
  variants: {
    opacity: {
      none: 'bg-transparent',
      lightest: 'bg-black/10',
      lighter: 'bg-black/20',
      light: 'bg-black/33',
      medium: 'bg-black/50',
      dark: 'bg-black/66',
      darker: 'bg-black/75',
      darkest: 'bg-black/85',
    },
    blur: {
      true: 'backdrop-blur-xs',
      false: 'backdrop-blur-none',
    },
  },
  defaultVariants: {
    opacity: 'medium',
  },
})

export type DrawerOverlayProps = React.ComponentPropsWithRef<
  typeof DrawerPrimitive.Overlay
> &
  VariantProps<typeof drawerOverlayVariants>

const DrawerOverlay = ({
  className,
  blur,
  opacity,
  ...props
}: DrawerOverlayProps) => (
  <DrawerPrimitive.Overlay
    className={cn(drawerOverlayVariants({ blur, opacity }), className)}
    {...props}
  />
)
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const drawerContentVariants = cva('fixed z-50 bg-white', {
  variants: {
    direction: {
      bottom: 'px-2xs pt-3xs pb-2xs inset-x-0 bottom-0 rounded-t-[32px]',
      top: 'px-2xs pt-2xs pb-3xs inset-x-0 top-0 rounded-b-[32px]',
      left: 'px-2xs pt-3xs pb-2xs inset-y-0 left-0 h-full w-full lg:max-w-[440px]',
      right:
        'px-2xs pt-3xs pb-2xs inset-y-0 right-0 h-full w-full lg:max-w-[440px]',
    },
  },
  defaultVariants: {
    direction: 'bottom',
  },
})

type DrawerContentProps = React.ComponentPropsWithRef<
  typeof DrawerPrimitive.Content
> & {
  overlayProps?: DrawerOverlayProps
  portal?: boolean
}

const DrawerContent = ({
  className,
  children,
  overlayProps,
  portal,
  ...props
}: DrawerContentProps) => {
  const direction = useDrawerDirection()
  const Container = portal === false ? React.Fragment : DrawerPrimitive.Portal

  return (
    <Container>
      <DrawerOverlay {...overlayProps} />
      <DrawerPrimitive.Content
        className={cn(drawerContentVariants({ direction }), className)}
        {...props}
      >
        {direction === 'bottom' && (
          <div className="mb-3xs mx-auto h-0.5 w-10 rounded-full bg-gray-200" />
        )}
        {children}
      </DrawerPrimitive.Content>
    </Container>
  )
}
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn('mb-3xs flex justify-between', className)}
      {...props}
    >
      {children}
    </div>
  )
}

const DrawerBody = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mb-2xs', className)} {...props}>
    {children}
  </div>
)

const DrawerFooter = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('gap-3xs flex flex-col', className)} {...props}>
    {children}
  </div>
)

const DrawerTitle = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof DrawerPrimitive.Title>) => (
  <DrawerPrimitive.Title
    className={cn('leading-distant text-xs font-medium', className)}
    {...props}
  />
)
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof DrawerPrimitive.Description>) => (
  <DrawerPrimitive.Description
    className={cn('text-2xs font-normal text-gray-500', className)}
    {...props}
  />
)
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerTrigger,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
}

export type { DrawerDirection, DrawerProps }
